import React, { useCallback, useMemo, useState } from "react";

import Dropdown from "antd/es/dropdown";
import { defineMessage, useIntl } from "react-intl";

import { MapFilterEntityType } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button } from "@mapmycustomers/ui";

import getAppliedMapFiltersCount from "@app/component/filtering/components/MapFilterButton/utils/getAppliedMapFiltersCount";
import getMapFilterTypeForAnalytics from "@app/component/filtering/components/MapFilterButton/utils/getMapFilterTypeForAnalytics";
import useMapView from "@app/component/view/utils/useMapView";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import AvailableEntityTypesMenu from "../../AvailableEntityTypesMenu";

import styles from "./Footer.module.scss";

const buttonLabel = defineMessage({
  id: "map.filters.selectTypeDropdown",
  defaultMessage: "+ Filter by another type",
  description: "Select filter type dropdown label in filter dropdown on Maps page",
});

interface Props {
  entityTypes: MapFilterEntityType[];
  filterModel: ModifiedMapFilterModel;
  hideSavedFilters?: boolean;
  implicitFilterFields?: IField[];
  onChange?: (
    callback: (previousFilterModel: ModifiedMapFilterModel) => ModifiedMapFilterModel
  ) => void;
  onCreateSavedFilter?: () => void;
  selectedFilter?: MapSavedFilter;
}

const Footer: React.FC<Props> = ({
  entityTypes,
  filterModel,
  hideSavedFilters,
  implicitFilterFields,
  onChange,
  onCreateSavedFilter,
  selectedFilter,
}) => {
  const analytics = useAnalytics();
  const filterByAnotherTypeAnalytics = useExtendedAnalytics("Filter by another type", analytics);
  const intl = useIntl();
  const {
    viewState: { filter, viewAs },
  } = useMapView();
  const [dropdownVisible, setDropdownVisibility] = useState(false);

  const availableEntityTypes = useMemo(() => {
    const usedEntityTypes = new Set(
      (Object.keys(filterModel) as MapFilterEntityType[]).filter((entityType) =>
        isDefined(filterModel[entityType])
      )
    );
    return entityTypes.filter((entityType) => !usedEntityTypes.has(entityType));
  }, [entityTypes, filterModel]);

  const handleSelectType = useCallback(
    (entityType) => {
      setDropdownVisibility(false);
      onChange?.((filters) => ({ ...filters, [entityType]: {} }));
      filterByAnotherTypeAnalytics.completed([getMapFilterTypeForAnalytics(entityType)]);
    },
    [filterByAnotherTypeAnalytics, onChange]
  );

  const handleVisibilityChange = useCallback(
    (visible: boolean) => {
      setDropdownVisibility(visible);
      if (visible) {
        filterByAnotherTypeAnalytics.initiated();
      } else {
        filterByAnotherTypeAnalytics.cancelled();
      }
    },
    [filterByAnotherTypeAnalytics]
  );

  const handleCreateSavedFilter = useCallback(() => {
    onCreateSavedFilter?.();
    analytics.clicked(["Save this filter"]);
  }, [analytics, onCreateSavedFilter]);

  return (
    <div className={styles.container}>
      {availableEntityTypes.length ? (
        <Dropdown
          onVisibleChange={handleVisibilityChange}
          overlay={
            <AvailableEntityTypesMenu items={availableEntityTypes} onSelect={handleSelectType} />
          }
          trigger={availableEntityTypes.length > 1 ? ["click"] : []}
          visible={dropdownVisible}
        >
          <Button
            disabled={availableEntityTypes.length === 0}
            onClick={
              availableEntityTypes.length === 1
                ? () => handleSelectType(availableEntityTypes[0])
                : undefined
            }
            size="small"
          >
            {intl.formatMessage(buttonLabel)}
          </Button>
        </Dropdown>
      ) : (
        // For some weird reason button was not getting disabled when it was a part of a Dropdown
        <>
          <Button disabled size="small">
            {intl.formatMessage(buttonLabel)}
          </Button>
        </>
      )}
      {!selectedFilter && !hideSavedFilters && (
        <Button
          disabled={!getAppliedMapFiltersCount(filter, viewAs, implicitFilterFields)}
          onClick={handleCreateSavedFilter}
          size="small"
          type="link"
        >
          {intl.formatMessage({
            id: "filters.save",
            defaultMessage: "Save this filter",
            description: "Save this filter button in filters dropdown",
          })}
        </Button>
      )}
    </div>
  );
};

export default Footer;
